<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('overtimeClock.title')" :description="t('overtimeClock.description')">
      <template #actions>
        <AppButton variant="outline" :disabled="loading" @click="refreshAll">
          <RefreshCw :class="['h-4 w-4 mr-2', loading ? 'animate-spin' : '']" />
          {{ t('overtimeClock.refresh') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Stat cards -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <AppStatCard :label="t('overtimeClock.pendingApproval')" :value="stats.pendingCount || 0" :icon="Clock" iconBg="bg-amber-50" iconColor="text-amber-600" />
      <AppStatCard :label="t('overtimeClock.activeSessions')" :value="stats.activeCount || 0" :icon="Timer" iconBg="bg-blue-50" iconColor="text-blue-600" />
      <AppStatCard :label="t('overtimeClock.needsReview')" :value="stats.reviewCount || 0" :icon="Eye" iconBg="bg-purple-50" iconColor="text-purple-600" />
      <AppStatCard :label="t('overtimeClock.pendingCost')" :value="formatCurrency(stats.pendingCost || 0)" :icon="DollarSign" iconBg="bg-emerald-50" iconColor="text-emerald-600" />
    </div>

    <!-- Tabs -->
    <div class="flex gap-1 border-b border-border">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        @click="activeTab = tab.key; if (tab.key === 'history') loadHistory()"
        :class="[
          'flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
          activeTab === tab.key
            ? 'border-primary text-primary'
            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
        ]"
      >
        <component :is="tab.icon" class="h-4 w-4" />
        {{ tab.label }}
        <span v-if="tab.count !== undefined" class="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">{{ tab.count }}</span>
      </button>
    </div>

    <!-- Review Queue Tab -->
    <div v-show="activeTab === 'review'">
      <div v-if="reviewQueue.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
        <CheckCircle class="h-12 w-12 text-emerald-400 mb-3" />
        <h3 class="text-base font-semibold text-foreground">{{ t('overtimeClock.allClear') }}</h3>
        <p class="text-sm text-muted-foreground mt-1">{{ t('overtimeClock.nothingToReview') }}</p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <AppCard
          v-for="session in reviewQueue"
          :key="session.id"
          :class="['overflow-hidden', session.clock_out_method === 'auto_expired' ? 'border-amber-300' : '']"
        >
          <!-- Urgent banner -->
          <div v-if="session.clock_out_method === 'auto_expired'" class="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-amber-700 text-xs font-semibold">
            <AlertTriangle class="h-3.5 w-3.5" />
            {{ t('overtimeClock.autoExpired') }}
          </div>

          <div class="p-4 flex flex-col gap-3">
            <!-- Employee + cost -->
            <div class="flex items-start justify-between gap-2">
              <div class="flex items-center gap-2.5">
                <div class="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                  {{ (session.employee_name || '?').split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase() }}
                </div>
                <div>
                  <p class="text-sm font-semibold text-foreground leading-tight">{{ session.employee_name }}</p>
                  <p class="text-xs text-muted-foreground">{{ session.employee_code }} · {{ session.department_name }}</p>
                </div>
              </div>
              <div class="text-sm font-bold text-foreground shrink-0">{{ formatCurrency(session.computed_cost || session.estimated_cost) }}</div>
            </div>

            <!-- Details -->
            <div class="space-y-1 text-xs">
              <div class="flex justify-between">
                <span class="text-muted-foreground">{{ t('overtimeClock.clockIn') }}</span>
                <span class="font-medium text-foreground">{{ formatDateTime(session.clock_in_timestamp) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-muted-foreground">{{ t('overtimeClock.clockOut') }}</span>
                <span class="font-medium text-foreground">{{ formatDateTime(session.clock_out_timestamp) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-muted-foreground">Duration</span>
                <span class="font-medium text-foreground">{{ formatDuration(session.actual_duration_minutes) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-muted-foreground">Rate</span>
                <span class="font-medium text-foreground">{{ session.overtime_rate_multiplier }}x</span>
              </div>
              <div v-if="session.anomaly_flags" class="flex items-center gap-1 text-amber-600">
                <AlertCircle class="h-3 w-3" />
                <span>{{ Object.keys(typeof session.anomaly_flags === 'string' ? JSON.parse(session.anomaly_flags) : session.anomaly_flags).length }} anomalies</span>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex flex-wrap gap-1.5 pt-1 border-t border-border">
              <AppButton size="sm" variant="outline" @click="viewDetails(session)">
                <Eye class="h-3.5 w-3.5 mr-1" />View
              </AppButton>
              <AppButton size="sm" variant="outline" @click="showEditModal(session)">
                <Pencil class="h-3.5 w-3.5 mr-1" />Edit
              </AppButton>
              <AppButton size="sm" variant="success" @click="showApprovalModal(session, 'payroll')">
                <CheckCircle class="h-3.5 w-3.5 mr-1" />Approve
              </AppButton>
              <AppButton size="sm" variant="destructive" @click="showAdminCancelModal(session)">
                <XCircle class="h-3.5 w-3.5 mr-1" />Reject
              </AppButton>
            </div>
          </div>
        </AppCard>
      </div>
    </div>

    <!-- Active Sessions Tab -->
    <div v-show="activeTab === 'active'">
      <div v-if="activeSessions.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
        <Timer class="h-12 w-12 text-muted-foreground/40 mb-3" />
        <h3 class="text-base font-semibold text-foreground">{{ t('overtimeClock.noActiveSessions') }}</h3>
        <p class="text-sm text-muted-foreground mt-1">{{ t('overtimeClock.noActiveSessionsDesc') }}</p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <AppCard v-for="session in activeSessions" :key="session.id" class="overflow-hidden border-blue-200">
          <div class="p-4 flex flex-col gap-3">
            <!-- Employee + timer -->
            <div class="flex items-start justify-between gap-2">
              <div class="flex items-center gap-2.5">
                <div class="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold shrink-0">
                  {{ (session.employee_name || '?').split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase() }}
                </div>
                <div>
                  <p class="text-sm font-semibold text-foreground leading-tight">{{ session.employee_name }}</p>
                  <p class="text-xs text-muted-foreground">{{ session.employee_code }} · {{ session.department_name }}</p>
                </div>
              </div>
              <div class="flex items-center gap-1 text-sm font-mono font-bold text-blue-600 shrink-0">
                <Timer class="h-3.5 w-3.5" />
                {{ session.formattedTimeRemaining }}
              </div>
            </div>

            <!-- Progress bar -->
            <div class="h-1.5 w-full rounded-full bg-blue-100 overflow-hidden">
              <div class="h-full rounded-full bg-blue-500 transition-all" :style="{ width: (session.progressPercentage || 0) + '%' }"></div>
            </div>

            <!-- Details -->
            <div class="space-y-1 text-xs">
              <div class="flex justify-between">
                <span class="text-muted-foreground">{{ t('overtimeClock.clockIn') }}</span>
                <span class="font-medium text-foreground">{{ formatDateTime(session.clock_in_timestamp) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-muted-foreground">{{ t('overtimeClock.timerEnds') }}</span>
                <span class="font-medium text-foreground">{{ formatDateTime(session.timer_end_time) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-muted-foreground">{{ t('overtimeClock.elapsed') }}</span>
                <span class="font-medium text-foreground">{{ formatDuration(Math.floor((session.elapsed_seconds || 0) / 60)) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-muted-foreground">{{ t('overtimeClock.estCost') }}</span>
                <span class="font-medium text-foreground">{{ formatCurrency(session.computed_cost || session.estimated_cost) }}</span>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex flex-wrap gap-1.5 pt-1 border-t border-border">
              <AppButton size="sm" variant="outline" @click="viewDetails(session)">
                <Eye class="h-3.5 w-3.5 mr-1" />View
              </AppButton>
              <AppButton size="sm" variant="outline" @click="showEditModal(session)">
                <Pencil class="h-3.5 w-3.5 mr-1" />Edit
              </AppButton>
              <AppButton size="sm" variant="warning" @click="showForceCloseModal(session)">
                <XCircle class="h-3.5 w-3.5 mr-1" />{{ t('overtimeClock.forceClose') }}
              </AppButton>
              <AppButton size="sm" variant="destructive" @click="showAdminCancelModal(session)">
                <XCircle class="h-3.5 w-3.5 mr-1" />Reject
              </AppButton>
            </div>
          </div>
        </AppCard>
      </div>
    </div>

    <!-- All Requests Tab -->
    <div v-show="activeTab === 'all'" class="flex flex-col gap-4">
      <!-- Filters -->
      <AppCard>
        <div class="p-4 flex flex-wrap gap-3 items-end">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">Status</label>
            <select v-model="filters.status" @change="loadAllRequests" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="">{{ t('overtimeClock.allStatuses') }}</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="under_review">Under Review</option>
              <option value="payroll_approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">From</label>
            <input type="date" v-model="filters.dateFrom" @change="loadAllRequests" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">To</label>
            <input type="date" v-model="filters.dateTo" @change="loadAllRequests" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <AppButton variant="outline" @click="clearFilters">
            <X class="h-4 w-4 mr-1" />Clear
          </AppButton>
        </div>
      </AppCard>

      <AppCard>
        <AppTable
          :columns="allRequestColumns"
          :rows="allRequests"
          :loading="loading"
          rowKey="id"
          :emptyText="t('overtimeClock.noRequests')"
        >
          <template #cell-employee="{ row }">
            <div class="flex items-center gap-2.5">
              <div class="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                {{ (row.employee_name || '?').split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase() }}
              </div>
              <div>
                <p class="text-sm font-medium text-foreground">{{ row.employee_name }}</p>
                <p class="text-xs text-muted-foreground">{{ row.employee_code }}</p>
              </div>
            </div>
          </template>
          <template #cell-clockIn="{ row }">
            <span class="text-sm text-foreground">{{ formatDateTime(row.clock_in_timestamp) }}</span>
          </template>
          <template #cell-clockOut="{ row }">
            <span class="text-sm text-foreground">{{ row.clock_out_timestamp ? formatDateTime(row.clock_out_timestamp) : '–' }}</span>
          </template>
          <template #cell-duration="{ row }">
            <span class="text-sm text-foreground">{{ formatDuration(row.actual_duration_minutes || row.requested_duration_minutes) }}</span>
          </template>
          <template #cell-type="{ row }">
            <AppBadge variant="secondary">{{ getSessionTypeLabel(row.session_type) }}</AppBadge>
          </template>
          <template #cell-status="{ row }">
            <AppBadge :variant="statusVariant(row.status)">{{ getStatusLabel(row.status) }}</AppBadge>
          </template>
          <template #cell-cost="{ row }">
            <span class="text-sm font-medium text-foreground">{{ formatCurrency(row.computed_cost || row.actual_cost || row.estimated_cost || 0) }}</span>
          </template>
          <template #actions="{ row }">
            <div class="flex items-center gap-1">
              <AppButton size="icon-sm" variant="ghost" title="View Details" @click.stop="viewDetails(row)">
                <Eye class="h-3.5 w-3.5" />
              </AppButton>
              <AppButton v-if="!['completed','rejected','cancelled'].includes(row.status)" size="icon-sm" variant="ghost" title="Edit" @click.stop="showEditModal(row)">
                <Pencil class="h-3.5 w-3.5" />
              </AppButton>
              <AppButton v-if="row.status === 'pending'" size="icon-sm" variant="ghost" class="text-emerald-600" title="Approve" @click.stop="showApprovalModal(row, 'clock-in')">
                <CheckCircle class="h-3.5 w-3.5" />
              </AppButton>
              <AppButton v-if="['under_review','expired'].includes(row.status)" size="icon-sm" variant="ghost" class="text-emerald-600" title="Approve for Payroll" @click.stop="showApprovalModal(row, 'payroll')">
                <DollarSign class="h-3.5 w-3.5" />
              </AppButton>
              <AppButton v-if="row.status === 'pending'" size="icon-sm" variant="ghost" class="text-destructive" title="Reject" @click.stop="showRejectModal(row)">
                <XCircle class="h-3.5 w-3.5" />
              </AppButton>
              <AppButton v-if="['active','under_review','expired'].includes(row.status)" size="icon-sm" variant="ghost" class="text-destructive" title="Reject Session" @click.stop="showAdminCancelModal(row)">
                <XCircle class="h-3.5 w-3.5" />
              </AppButton>
            </div>
          </template>
        </AppTable>

        <!-- Pagination -->
        <div v-if="pagination.totalPages > 1" class="flex items-center justify-between px-4 py-3 border-t border-border">
          <span class="text-sm text-muted-foreground">Page {{ pagination.page }} of {{ pagination.totalPages }}</span>
          <div class="flex items-center gap-1">
            <AppButton size="icon-sm" variant="outline" :disabled="pagination.page === 1" @click="changePage(pagination.page - 1)">
              <ChevronLeft class="h-3.5 w-3.5" />
            </AppButton>
            <AppButton size="icon-sm" variant="outline" :disabled="pagination.page === pagination.totalPages" @click="changePage(pagination.page + 1)">
              <ChevronRight class="h-3.5 w-3.5" />
            </AppButton>
          </div>
        </div>
      </AppCard>
    </div>

    <!-- History Tab -->
    <div v-show="activeTab === 'history'" class="flex flex-col gap-4">
      <!-- Filters -->
      <AppCard>
        <div class="p-4 flex flex-wrap gap-3 items-end">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">Search Employee</label>
            <input type="text" v-model="historyFilters.search" @input="loadHistory" placeholder="Name…" class="h-9 w-44 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">From</label>
            <input type="date" v-model="historyFilters.dateFrom" @change="loadHistory" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">To</label>
            <input type="date" v-model="historyFilters.dateTo" @change="loadHistory" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <AppButton variant="outline" @click="clearHistoryFilters">
            <X class="h-4 w-4 mr-1" />Clear
          </AppButton>
        </div>
      </AppCard>

      <AppCard>
        <AppTable
          :columns="historyColumns"
          :rows="historyRecords"
          :loading="loading"
          rowKey="id"
          emptyText="No history records found"
        >
          <template #cell-employee="{ row }">
            <div>
              <p class="text-sm font-medium text-foreground">{{ row.employee_name }}</p>
              <p class="text-xs text-muted-foreground">{{ row.employee_code }}</p>
            </div>
          </template>
          <template #cell-date="{ row }">
            <span class="text-sm text-foreground">{{ formatHistoryDate(row.scheduled_date || row.clock_in_timestamp) }}</span>
          </template>
          <template #cell-clockIn="{ row }">
            <span class="text-sm font-mono text-foreground">{{ row.clock_in_timestamp ? formatTime(row.clock_in_timestamp) : '–' }}</span>
          </template>
          <template #cell-clockOut="{ row }">
            <span class="text-sm font-mono text-foreground">{{ row.clock_out_timestamp ? formatTime(row.clock_out_timestamp) : '–' }}</span>
          </template>
          <template #cell-method="{ row }">
            <span v-if="row.clock_out_method" :class="['text-xs font-medium', clockOutMethodClass(row.clock_out_method)]">{{ formatClockOutMethod(row.clock_out_method) }}</span>
            <span v-else class="text-xs text-muted-foreground">–</span>
          </template>
          <template #cell-duration="{ row }">
            <span class="text-sm text-foreground">{{ formatDuration(row.actual_duration_minutes || row.requested_duration_minutes) }}</span>
          </template>
          <template #cell-cost="{ row }">
            <span class="text-sm text-foreground">{{ formatCurrency(row.actual_cost || row.estimated_cost || 0) }}</span>
          </template>
          <template #cell-status="{ row }">
            <AppBadge :variant="statusVariant(row.status)">{{ getStatusLabel(row.status) }}</AppBadge>
          </template>
          <template #actions="{ row }">
            <AppButton size="icon-sm" variant="ghost" @click.stop="viewDetails(row)">
              <Eye class="h-3.5 w-3.5" />
            </AppButton>
          </template>
        </AppTable>

        <!-- History pagination -->
        <div v-if="historyPagination.totalPages > 1" class="flex items-center justify-between px-4 py-3 border-t border-border">
          <span class="text-sm text-muted-foreground">Page {{ historyPagination.page }} of {{ historyPagination.totalPages }}</span>
          <div class="flex items-center gap-1">
            <AppButton size="icon-sm" variant="outline" :disabled="historyPagination.page === 1" @click="changeHistoryPage(historyPagination.page - 1)">
              <ChevronLeft class="h-3.5 w-3.5" />
            </AppButton>
            <AppButton size="icon-sm" variant="outline" :disabled="historyPagination.page === historyPagination.totalPages" @click="changeHistoryPage(historyPagination.page + 1)">
              <ChevronRight class="h-3.5 w-3.5" />
            </AppButton>
          </div>
        </div>
      </AppCard>
    </div>

    <!-- Details Modal -->
    <AppModal v-model="detailsModal.show" title="Session Details" size="xl">
      <div v-if="detailsModal.session" class="space-y-5">

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Employee info -->
          <div class="rounded-lg bg-muted/50 p-4">
            <p class="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Employee</p>
            <div class="space-y-1.5 text-sm">
              <div><span class="text-muted-foreground">Name:</span> <span class="font-medium ml-1">{{ detailsModal.session.employee_name }}</span></div>
              <div><span class="text-muted-foreground">Code:</span> <span class="font-medium ml-1">{{ detailsModal.session.employee_code }}</span></div>
              <div><span class="text-muted-foreground">Department:</span> <span class="font-medium ml-1">{{ detailsModal.session.department_name }}</span></div>
            </div>
          </div>

          <!-- Session info -->
          <div class="rounded-lg bg-muted/50 p-4">
            <p class="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Session</p>
            <div class="space-y-1.5 text-sm">
              <div class="flex items-center gap-2">
                <span class="text-muted-foreground">Status:</span>
                <AppBadge :variant="statusVariant(detailsModal.session.status)">{{ getStatusLabel(detailsModal.session.status) }}</AppBadge>
              </div>
              <div><span class="text-muted-foreground">Type:</span> <span class="font-medium ml-1">{{ detailsModal.session.session_type }}</span></div>
              <div><span class="text-muted-foreground">Priority:</span> <span class="font-medium ml-1 capitalize">{{ detailsModal.session.priority }}</span></div>
            </div>
          </div>
        </div>

        <!-- Time + financial -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="rounded-lg border border-border p-4">
            <p class="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Time Details</p>
            <div class="space-y-1.5 text-sm">
              <div><span class="text-muted-foreground">Clock In:</span> <span class="font-medium ml-1">{{ formatDateTime(detailsModal.session.clock_in_timestamp) }}</span></div>
              <div><span class="text-muted-foreground">Clock Out:</span> <span class="font-medium ml-1">{{ detailsModal.session.clock_out_timestamp ? formatDateTime(detailsModal.session.clock_out_timestamp) : 'Not clocked out' }}</span></div>
              <div v-if="detailsModal.session.clock_out_method">
                <span class="text-muted-foreground">Method:</span>
                <span class="font-medium ml-1" :class="clockOutMethodClass(detailsModal.session.clock_out_method)">{{ formatClockOutMethod(detailsModal.session.clock_out_method) }}</span>
              </div>
              <div><span class="text-muted-foreground">Requested:</span> <span class="font-medium ml-1">{{ formatDuration(detailsModal.session.requested_duration_minutes) }}</span></div>
              <div v-if="detailsModal.session.actual_duration_minutes"><span class="text-muted-foreground">Actual:</span> <span class="font-medium ml-1">{{ formatDuration(detailsModal.session.actual_duration_minutes) }}</span></div>
            </div>
          </div>
          <div class="rounded-lg border border-border p-4">
            <p class="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Financial</p>
            <div class="space-y-1.5 text-sm">
              <div><span class="text-muted-foreground">Rate Multiplier:</span> <span class="font-medium ml-1">{{ detailsModal.session.overtime_rate_multiplier }}x</span></div>
              <div><span class="text-muted-foreground">Estimated Cost:</span> <span class="font-medium ml-1">{{ formatCurrency(detailsModal.session.estimated_cost) }}</span></div>
              <div v-if="detailsModal.session.actual_cost"><span class="text-muted-foreground">Actual Cost:</span> <span class="font-bold text-primary ml-1">{{ formatCurrency(detailsModal.session.actual_cost) }}</span></div>
            </div>
          </div>
        </div>

        <!-- Reason -->
        <div class="text-sm">
          <p class="text-xs text-muted-foreground mb-1">Reason</p>
          <div class="rounded-lg bg-muted/50 p-3 text-foreground">{{ detailsModal.session.reason }}</div>
        </div>

        <!-- Evidence -->
        <div>
          <p class="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Evidence</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EvidenceDisplay
              label="Clock In"
              type="clock_in"
              :photoUrl="detailsModal.evidence?.clockIn?.photo?.url"
              :latitude="detailsModal.evidence?.clockIn?.location?.latitude"
              :longitude="detailsModal.evidence?.clockIn?.location?.longitude"
              :address="detailsModal.evidence?.clockIn?.location?.address"
              :deviceFingerprint="detailsModal.evidence?.clockIn?.device?.fingerprint"
              :timestamp="detailsModal.evidence?.clockIn?.timestamp"
              :showDevice="true"
            />
            <EvidenceDisplay
              v-if="detailsModal.evidence?.clockOut?.photo?.available || detailsModal.session.clock_out_timestamp"
              label="Clock Out"
              type="clock_out"
              :photoUrl="detailsModal.evidence?.clockOut?.photo?.url"
              :latitude="detailsModal.evidence?.clockOut?.location?.latitude"
              :longitude="detailsModal.evidence?.clockOut?.location?.longitude"
              :address="detailsModal.evidence?.clockOut?.location?.address"
              :deviceFingerprint="detailsModal.evidence?.clockOut?.device?.fingerprint"
              :timestamp="detailsModal.evidence?.clockOut?.timestamp"
              :showDevice="true"
            />
          </div>

          <!-- Maps -->
          <div v-if="detailsModal.evidence?.clockIn?.location?.latitude" class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <div class="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                <MapPin class="h-3.5 w-3.5 text-emerald-600" />
                <span>Clock In Location</span>
                <select v-model="mapProvider" class="ml-auto h-7 rounded border border-input bg-background px-2 text-xs focus:outline-none">
                  <option value="google">Google</option>
                  <option value="osm">OSM</option>
                  <option value="google-satellite">Satellite</option>
                </select>
              </div>
              <iframe :src="getMapUrl(detailsModal.evidence.clockIn.location.latitude, detailsModal.evidence.clockIn.location.longitude)" class="w-full rounded-lg border border-border" style="height:180px" frameborder="0" allowfullscreen loading="lazy" title="Clock In Location"></iframe>
              <p class="text-xs text-muted-foreground mt-1">{{ parseFloat(detailsModal.evidence.clockIn.location.latitude).toFixed(6) }}, {{ parseFloat(detailsModal.evidence.clockIn.location.longitude).toFixed(6) }}</p>
            </div>
            <div v-if="detailsModal.evidence?.clockOut?.location?.latitude">
              <div class="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                <MapPin class="h-3.5 w-3.5 text-red-600" />
                <span>Clock Out Location</span>
                <select v-model="mapProvider" class="ml-auto h-7 rounded border border-input bg-background px-2 text-xs focus:outline-none">
                  <option value="google">Google</option>
                  <option value="osm">OSM</option>
                  <option value="google-satellite">Satellite</option>
                </select>
              </div>
              <iframe :src="getMapUrl(detailsModal.evidence.clockOut.location.latitude, detailsModal.evidence.clockOut.location.longitude)" class="w-full rounded-lg border border-border" style="height:180px" frameborder="0" allowfullscreen loading="lazy" title="Clock Out Location"></iframe>
              <p class="text-xs text-muted-foreground mt-1">{{ parseFloat(detailsModal.evidence.clockOut.location.latitude).toFixed(6) }}, {{ parseFloat(detailsModal.evidence.clockOut.location.longitude).toFixed(6) }}</p>
            </div>
          </div>

          <!-- Anomalies -->
          <div v-if="detailsModal.evidence?.anomalies?.length" class="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3">
            <p class="flex items-center gap-2 text-amber-700 text-xs font-semibold mb-2">
              <AlertTriangle class="h-3.5 w-3.5" />
              {{ detailsModal.evidence.anomalies.length }} Anomalies Detected
            </p>
            <div class="flex flex-wrap gap-1.5">
              <span v-for="(flag, idx) in detailsModal.evidence.anomalies" :key="idx" class="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                {{ typeof flag === 'string' ? flag : flag.type || flag.name || JSON.stringify(flag) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </AppModal>

    <!-- Approval Modal -->
    <AppModal v-model="approvalModal.show" :title="approvalModal.type === 'clock-in' ? 'Approve Overtime Clock-In' : 'Approve for Payroll'" size="sm">
      <div class="space-y-4">
        <p class="text-sm text-muted-foreground">
          {{ approvalModal.type === 'clock-in' ? 'Approve this overtime request to start the timer' : 'Approve this session for payroll inclusion' }}
        </p>
        <div v-if="approvalModal.type === 'clock-in'" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Rate Multiplier (optional)</label>
          <input v-model.number="approvalModal.rateMultiplier" type="number" step="0.1" min="1" max="5" placeholder="Leave empty for default rate" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <p class="text-xs text-muted-foreground">Default: Based on overtime policy</p>
        </div>
        <div v-if="approvalModal.type === 'payroll'" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Adjusted Hours (optional)</label>
          <input v-model.number="approvalModal.adjustedHours" type="number" step="0.25" min="0" placeholder="Leave empty to use actual duration" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <p class="text-xs text-muted-foreground">Override the duration if needed</p>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Notes (optional)</label>
          <textarea v-model="approvalModal.notes" rows="3" placeholder="Add any notes about this approval…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="approvalModal.show = false">Cancel</AppButton>
          <AppButton variant="success" :disabled="processing" @click="approveSession">
            <CheckCircle class="h-4 w-4 mr-1.5" />
            {{ processing ? 'Approving…' : 'Approve' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Reject Modal -->
    <AppModal v-model="rejectModal.show" title="Reject Overtime Request" size="sm">
      <div class="space-y-4">
        <p class="text-sm text-muted-foreground">Please provide a reason for rejecting this overtime request:</p>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Reason <span class="text-destructive">*</span></label>
          <textarea v-model="rejectModal.reason" rows="4" placeholder="Explain why this request is being rejected…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="rejectModal.show = false">Cancel</AppButton>
          <AppButton variant="destructive" :disabled="!rejectModal.reason || processing" @click="rejectSession">
            <XCircle class="h-4 w-4 mr-1.5" />
            {{ processing ? 'Rejecting…' : 'Reject Request' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Force Close Modal -->
    <AppModal v-model="forceCloseModal.show" title="Force Close Overtime Session" size="sm">
      <div class="space-y-4">
        <div class="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2.5 text-sm text-amber-700">
          <AlertTriangle class="h-4 w-4 shrink-0 mt-0.5" />
          <p>This will immediately end the employee's overtime session and create an attendance record for the elapsed time. The session will move to the review queue for payroll approval.</p>
        </div>
        <div v-if="forceCloseModal.session" class="space-y-1.5 text-sm">
          <div><span class="text-muted-foreground">Employee:</span> <span class="font-medium ml-1">{{ forceCloseModal.session.employee_name }}</span></div>
          <div><span class="text-muted-foreground">Time Remaining:</span> <span class="font-medium ml-1">{{ forceCloseModal.session.formattedTimeRemaining }}</span></div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Reason <span class="text-destructive">*</span></label>
          <textarea v-model="forceCloseModal.reason" rows="4" placeholder="Explain why this session is being force-closed…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="forceCloseModal.show = false">Cancel</AppButton>
          <AppButton variant="destructive" :disabled="!forceCloseModal.reason?.trim() || processing" @click="forceCloseSession">
            <XCircle class="h-4 w-4 mr-1.5" />
            {{ processing ? 'Closing…' : 'Force Close' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Admin Cancel/Reject Modal -->
    <AppModal v-model="adminCancelModal.show" title="Reject Overtime Session" size="sm">
      <div class="space-y-4">
        <div class="rounded-lg bg-red-50 border border-red-200 p-3 flex gap-2.5 text-sm text-red-700">
          <AlertTriangle class="h-4 w-4 shrink-0 mt-0.5" />
          <p>This will permanently reject this overtime session. The employee will be notified. This action cannot be undone.</p>
        </div>
        <div v-if="adminCancelModal.session" class="space-y-1.5 text-sm">
          <div><span class="text-muted-foreground">Employee:</span> <span class="font-medium ml-1">{{ adminCancelModal.session.employee_name }}</span></div>
          <div class="flex items-center gap-2">
            <span class="text-muted-foreground">Status:</span>
            <AppBadge :variant="statusVariant(adminCancelModal.session.status)">{{ getStatusLabel(adminCancelModal.session.status) }}</AppBadge>
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Reason <span class="text-destructive">*</span></label>
          <textarea v-model="adminCancelModal.reason" rows="4" placeholder="Explain why this session is being rejected…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="adminCancelModal.show = false">Cancel</AppButton>
          <AppButton variant="destructive" :disabled="!adminCancelModal.reason?.trim() || processing" @click="adminCancelSession">
            <XCircle class="h-4 w-4 mr-1.5" />
            {{ processing ? 'Rejecting…' : 'Reject Session' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Edit Session Modal -->
    <AppModal v-model="editModal.show" title="Edit Overtime Session" size="sm">
      <div class="space-y-4">
        <div v-if="editModal.session" class="space-y-1.5 text-sm">
          <div><span class="text-muted-foreground">Employee:</span> <span class="font-medium ml-1">{{ editModal.session.employee_name }}</span></div>
          <div class="flex items-center gap-2">
            <span class="text-muted-foreground">Status:</span>
            <AppBadge :variant="statusVariant(editModal.session.status)">{{ getStatusLabel(editModal.session.status) }}</AppBadge>
          </div>
        </div>
        <div v-if="editModal.session?.clock_out_timestamp" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Actual Duration (hours)</label>
          <input v-model.number="editModal.actualDurationHours" type="number" step="0.25" min="0" max="24" placeholder="e.g. 2.5 for 2h 30m" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <p class="text-xs text-muted-foreground">Leave empty to keep current value</p>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Rate Multiplier</label>
          <input v-model.number="editModal.rateMultiplier" type="number" step="0.1" min="1" max="5" placeholder="e.g. 1.5" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Reason</label>
          <textarea v-model="editModal.reason" rows="3" placeholder="Overtime reason…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Admin Notes</label>
          <textarea v-model="editModal.notes" rows="3" placeholder="Internal admin notes…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="editModal.show = false">Cancel</AppButton>
          <AppButton :disabled="processing" @click="saveEditSession">
            <CheckCircle class="h-4 w-4 mr-1.5" />
            {{ processing ? 'Saving…' : 'Save Changes' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Toast -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="toast.show" :class="['fixed bottom-4 right-4 z-[80] flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg text-sm font-medium', toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white']">
          <CheckCircle v-if="toast.type === 'success'" class="h-4 w-4 shrink-0" />
          <AlertCircle v-else class="h-4 w-4 shrink-0" />
          {{ toast.message }}
        </div>
      </Transition>
    </Teleport>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Clock,
  Timer,
  Eye,
  DollarSign,
  RefreshCw,
  User,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  X,
  Calendar,
  List,
  ChevronLeft,
  ChevronRight,
  XCircle,
  History,
  MapPin,
  Pencil,
} from 'lucide-vue-next';
import EvidenceDisplay from '@/components/shared/EvidenceDisplay.vue';
import { AppPageHeader, AppStatCard, AppCard, AppTable, AppBadge, AppButton, AppModal } from '@/components/ui';
import { api } from '@/services/api';
import { useCurrency } from '@/composables/useCurrency';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const { formatCurrency, loadCurrency } = useCurrency();
const { t } = useI18n();

// Tab definitions
const tabs = computed(() => [
  { key: 'review',  label: t('overtimeClock.needsReview'),    icon: Eye,     count: stats.value.reviewCount || 0 },
  { key: 'active',  label: t('overtimeClock.activeSessions'), icon: Timer,   count: stats.value.activeCount || 0 },
  { key: 'all',     label: t('common.all'),                   icon: List },
  { key: 'history', label: t('empHistory.overtime'),          icon: History },
]);

// Table column definitions
const allRequestColumns = computed(() => [
  { key: 'employee', label: t('overtimeClock.employee') },
  { key: 'clockIn',  label: t('overtimeClock.clockIn'),  mobileHide: true },
  { key: 'clockOut', label: t('overtimeClock.clockOut'), mobileHide: true },
  { key: 'duration', label: t('overtimeClock.duration') },
  { key: 'type',     label: t('overtimeClock.type'),     mobileHide: true },
  { key: 'status',   label: t('overtimeClock.status') },
  { key: 'cost',     label: t('overtimeClock.cost'),     mobileHide: true },
]);

const historyColumns = [
  { key: 'employee', label: 'Employee' },
  { key: 'date',     label: 'Date' },
  { key: 'clockIn',  label: 'Clock In',  mobileHide: true },
  { key: 'clockOut', label: 'Clock Out', mobileHide: true },
  { key: 'method',   label: 'Method',    mobileHide: true },
  { key: 'duration', label: 'Duration' },
  { key: 'cost',     label: 'Cost',      mobileHide: true },
  { key: 'status',   label: 'Status' },
];

const statusVariant = (status) => {
  const map = {
    pending: 'warning',
    active: 'default',
    under_review: 'warning',
    payroll_approved: 'success',
    completed: 'success',
    rejected: 'destructive',
    cancelled: 'muted',
    expired: 'muted',
  };
  return map[status] || 'muted';
};

// Reactive state
const activeTab = ref('all');
const loading = ref(false);
const processing = ref(false);

const stats = ref({
  pendingCount: 0,
  activeCount: 0,
  reviewCount: 0,
  pendingCost: 0
});

const reviewQueue = ref([]);
const activeSessions = ref([]);
const allRequests = ref([]);

const filters = ref({
  status: '',
  dateFrom: '',
  dateTo: ''
});

const pagination = ref({
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0
});

const mapProvider = ref('google');

const detailsModal = ref({
  show: false,
  session: null,
  evidence: null
});

const approvalModal = ref({
  show: false,
  type: 'clock-in', // or 'payroll'
  session: null,
  rateMultiplier: null,
  adjustedHours: null,
  notes: ''
});

const rejectModal = ref({
  show: false,
  session: null,
  reason: ''
});

const forceCloseModal = ref({
  show: false,
  session: null,
  reason: ''
});

const adminCancelModal = ref({
  show: false,
  session: null,
  reason: ''
});

const editModal = ref({
  show: false,
  session: null,
  actualDurationHours: null,
  rateMultiplier: null,
  reason: '',
  notes: ''
});

const historyRecords = ref([]);
const historyFilters = ref({
  search: '',
  dateFrom: '',
  dateTo: ''
});
const historyPagination = ref({
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0
});

const toast = ref({
  show: false,
  message: '',
  type: 'success'
});

let refreshInterval = null;

// Methods
const loadStats = async () => {
  try {
    const [reviewResponse, activeResponse] = await Promise.all([
      api.get('/admin/overtime-clock/review-queue', { params: { limit: 1 } }),
      api.get('/admin/overtime-clock/active-sessions')
    ]);

    stats.value.reviewCount = reviewResponse.data.data.pagination?.total || 0;
    stats.value.activeCount = activeResponse.data.data.count || 0;

    const allPendingResponse = await api.get('/admin/overtime-clock/requests', {
      params: { status: 'pending' }
    });
    stats.value.pendingCount = allPendingResponse.data.data.pagination?.total || 0;

    // Calculate pending cost
    const sessions = allPendingResponse.data.data.sessions || [];
    stats.value.pendingCost = sessions.reduce((sum, s) => sum + (s.computed_cost || s.estimated_cost || 0), 0);
  } catch (error) {
    console.error('Failed to load stats:', error);
  }
};

const loadReviewQueue = async () => {
  try {
    const response = await api.get('/admin/overtime-clock/review-queue', {
      params: { limit: 50 }
    });
    reviewQueue.value = response.data.data.sessions || [];
  } catch (error) {
    console.error('Failed to load review queue:', error);
  }
};

const loadActiveSessions = async () => {
  try {
    const response = await api.get('/admin/overtime-clock/active-sessions');
    activeSessions.value = response.data.data.sessions || [];
  } catch (error) {
    console.error('Failed to load active sessions:', error);
  }
};

const loadAllRequests = async () => {
  try {
    const response = await api.get('/admin/overtime-clock/requests', {
      params: {
        ...filters.value,
        page: pagination.value.page,
        limit: pagination.value.limit
      }
    });

    allRequests.value = response.data.data.sessions || [];
    pagination.value = response.data.data.pagination || pagination.value;
  } catch (error) {
    console.error('Failed to load all requests:', error);
  }
};

const loadHistory = async () => {
  try {
    const params = {
      status: 'completed,payroll_approved,under_review',
      page: historyPagination.value.page,
      limit: historyPagination.value.limit
    };
    if (historyFilters.value.dateFrom) params.dateFrom = historyFilters.value.dateFrom;
    if (historyFilters.value.dateTo) params.dateTo = historyFilters.value.dateTo;

    const response = await api.get('/admin/overtime-clock/requests', { params });
    let sessions = response.data.data.sessions || [];

    // Client-side search filter by employee name
    if (historyFilters.value.search) {
      const q = historyFilters.value.search.toLowerCase();
      sessions = sessions.filter(s =>
        (s.employee_name || '').toLowerCase().includes(q) ||
        (s.employee_code || '').toLowerCase().includes(q)
      );
    }

    historyRecords.value = sessions;
    historyPagination.value = {
      ...historyPagination.value,
      ...response.data.data.pagination
    };
  } catch (error) {
    console.error('Failed to load history:', error);
  }
};

const clearHistoryFilters = () => {
  historyFilters.value = { search: '', dateFrom: '', dateTo: '' };
  historyPagination.value.page = 1;
  loadHistory();
};

const changeHistoryPage = (page) => {
  historyPagination.value.page = page;
  loadHistory();
};

const refreshAll = async () => {
  loading.value = true;
  try {
    await Promise.all([
      loadStats(),
      activeTab.value === 'review' ? loadReviewQueue() : Promise.resolve(),
      activeTab.value === 'active' ? loadActiveSessions() : Promise.resolve(),
      activeTab.value === 'all' ? loadAllRequests() : Promise.resolve(),
      activeTab.value === 'history' ? loadHistory() : Promise.resolve()
    ]);
  } finally {
    loading.value = false;
  }
};

const viewDetails = async (session) => {
  try {
    const response = await api.get(`/admin/overtime-clock/${session.id}`);
    detailsModal.value.session = response.data.data.session;
    detailsModal.value.evidence = response.data.data.evidence || null;
    detailsModal.value.show = true;
  } catch (error) {
    showToast('Failed to load session details', 'error');
  }
};

const showApprovalModal = (session, type = 'clock-in') => {
  approvalModal.value = {
    show: true,
    type,
    session,
    rateMultiplier: null,
    adjustedHours: null,
    notes: ''
  };
};

const showRejectModal = (session) => {
  rejectModal.value = {
    show: true,
    session,
    reason: ''
  };
};

const approveSession = async () => {
  processing.value = true;
  try {
    const { session, type, rateMultiplier, adjustedHours, notes } = approvalModal.value;

    if (type === 'clock-in') {
      await api.post(`/admin/overtime-clock/${session.id}/approve`, {
        adjustedRateMultiplier: rateMultiplier,
        notes
      });
      showToast('Overtime clock-in approved! Timer started.', 'success');
    } else {
      await api.post(`/admin/overtime-clock/${session.id}/approve-payroll`, {
        adjustedHours,
        notes
      });
      showToast('Session approved for payroll', 'success');
    }

    approvalModal.value.show = false;
    await refreshAll();
  } catch (error) {
    showToast(error.response?.data?.message || 'Approval failed', 'error');
  } finally {
    processing.value = false;
  }
};

const rejectSession = async () => {
  processing.value = true;
  try {
    const { session, reason } = rejectModal.value;

    await api.post(`/admin/overtime-clock/${session.id}/reject`, {
      reason
    });

    showToast('Overtime request rejected', 'success');
    rejectModal.value.show = false;
    await refreshAll();
  } catch (error) {
    showToast(error.response?.data?.message || 'Rejection failed', 'error');
  } finally {
    processing.value = false;
  }
};

const showForceCloseModal = (session) => {
  forceCloseModal.value = {
    show: true,
    session,
    reason: ''
  };
};

const forceCloseSession = async () => {
  processing.value = true;
  try {
    const { session, reason } = forceCloseModal.value;

    await api.post(`/admin/overtime-clock/${session.id}/force-close`, {
      reason: reason.trim()
    });

    showToast('Overtime session force-closed successfully', 'success');
    forceCloseModal.value.show = false;
    await refreshAll();
  } catch (error) {
    showToast(error.response?.data?.message || 'Force close failed', 'error');
  } finally {
    processing.value = false;
  }
};

const showAdminCancelModal = (session) => {
  adminCancelModal.value = {
    show: true,
    session,
    reason: ''
  };
};

const adminCancelSession = async () => {
  processing.value = true;
  try {
    const { session, reason } = adminCancelModal.value;

    await api.post(`/admin/overtime-clock/${session.id}/admin-cancel`, {
      reason: reason.trim()
    });

    showToast('Overtime session rejected', 'success');
    adminCancelModal.value.show = false;
    await refreshAll();
  } catch (error) {
    showToast(error.response?.data?.message || 'Rejection failed', 'error');
  } finally {
    processing.value = false;
  }
};

const showEditModal = (session) => {
  editModal.value = {
    show: true,
    session,
    actualDurationHours: session.actual_duration_minutes ? +(session.actual_duration_minutes / 60).toFixed(2) : null,
    rateMultiplier: session.overtime_rate_multiplier || null,
    reason: session.reason || '',
    notes: session.admin_review_notes || ''
  };
};

const saveEditSession = async () => {
  processing.value = true;
  try {
    const { session, actualDurationHours, rateMultiplier, reason, notes } = editModal.value;

    const payload = {};
    if (actualDurationHours !== null && actualDurationHours !== '') {
      payload.actualDurationMinutes = Math.round(parseFloat(actualDurationHours) * 60);
    }
    if (rateMultiplier !== null && rateMultiplier !== '') {
      payload.rateMultiplier = parseFloat(rateMultiplier);
    }
    if (reason !== undefined) payload.reason = reason;
    if (notes !== undefined) payload.notes = notes;

    await api.put(`/admin/overtime-clock/${session.id}`, payload);

    showToast('Overtime session updated successfully', 'success');
    editModal.value.show = false;
    await refreshAll();
  } catch (error) {
    showToast(error.response?.data?.message || 'Update failed', 'error');
  } finally {
    processing.value = false;
  }
};

const clearFilters = () => {
  filters.value = {
    status: '',
    dateFrom: '',
    dateTo: ''
  };
  pagination.value.page = 1;
  loadAllRequests();
};

const changePage = (page) => {
  pagination.value.page = page;
  loadAllRequests();
};

const showToast = (message, type = 'success') => {
  toast.value = { show: true, message, type };
  setTimeout(() => {
    toast.value.show = false;
  }, 4000);
};

const getSessionTypeLabel = (type) => {
  const labels = {
    post_shift: 'Post-Shift',
    pre_shift: 'Pre-Shift',
    weekend: 'Weekend',
    holiday: 'Holiday',
    emergency: 'Emergency'
  };
  return labels[type] || type || 'Standard';
};

const getStatusLabel = (status) => {
  const labels = {
    pending: 'Pending',
    approved: 'Approved (Awaiting Activation)',
    active: 'Active',
    under_review: 'Under Review',
    completed: 'Completed',
    expired: 'Expired',
    rejected: 'Rejected',
    payroll_approved: 'Approved'
  };
  return labels[status] || status;
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '-';
  // Timestamps are stored as Palestine time but returned with Z suffix by mysql2;
  // use .utc() to display the raw stored value (which IS Palestine time)
  return dayjs.utc(timestamp).format('MMM D, h:mm A');
};

const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatClockOutMethod = (method) => {
  const methods = {
    manual: 'Manual (Employee)',
    auto_expired: 'Auto-Capped (Timer Expired)',
    admin_force_close: 'Admin Force Close'
  };
  return methods[method] || method;
};

const formatHistoryDate = (timestamp) => {
  if (!timestamp) return '-';
  return dayjs.utc(timestamp).format('MMM D, YYYY');
};

const formatTime = (timestamp) => {
  if (!timestamp) return '-';
  return dayjs.utc(timestamp).format('h:mm A');
};

const getMapUrl = (lat, lng) => {
  const zoom = 16;
  switch (mapProvider.value) {
    case 'google':
      return `https://maps.google.com/maps?q=${lat},${lng}&hl=en&z=${zoom}&output=embed`;
    case 'google-satellite':
      return `https://maps.google.com/maps?q=${lat},${lng}&hl=en&z=${zoom}&t=k&output=embed`;
    case 'osm':
      return `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01},${lat-0.01},${lng+0.01},${lat+0.01}&layer=mapnik&marker=${lat},${lng}`;
    default:
      return `https://maps.google.com/maps?q=${lat},${lng}&hl=en&z=${zoom}&output=embed`;
  }
};

const clockOutMethodClass = (method) => {
  if (method === 'manual') return 'text-green-600 font-medium';
  if (method === 'auto_expired') return 'text-orange-600 font-medium';
  if (method === 'admin_force_close') return 'text-red-600 font-medium';
  return '';
};

// Lifecycle
onMounted(async () => {
  await loadCurrency();
  await refreshAll();

  // Refresh active sessions every 30 seconds
  refreshInterval = setInterval(async () => {
    if (activeTab.value === 'active') {
      await loadActiveSessions();
    }
    await loadStats();
  }, 30000);
});

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
});
</script>

<style scoped>
.overtime-clock-management {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.page-header h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
}

.page-header p {
  color: #6b7280;
  margin: 0;
}

.btn-refresh {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-refresh:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.stat-icon.pending {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.stat-icon.active {
  background: linear-gradient(135deg, #10b981, #059669);
}

.stat-icon.review {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.stat-icon.approved {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.stat-content h3 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.stat-content p {
  color: #6b7280;
  margin: 0;
  font-size: 0.875rem;
}

.tabs-container {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.tabs {
  display: flex;
  gap: 0.5rem;
}

.tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab:hover {
  background: #f3f4f6;
  color: #374151;
}

.tab.active {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #9ca3af;
}

.empty-state svg {
  margin: 0 auto 1rem;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

.sessions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.session-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 2px solid transparent;
  transition: all 0.2s ease;
  position: relative;
}

.session-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.session-card.review {
  border-color: #dbeafe;
}

.session-card.review.urgent {
  border-color: #fed7aa;
}

.session-card.active {
  border-color: #d1fae5;
}

.urgent-badge {
  position: absolute;
  top: -10px;
  right: 1rem;
  background: #f59e0b;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.employee-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.employee-info svg {
  color: #667eea;
}

.employee-info h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.employee-info p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.session-cost,
.timer-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #8b5cf6;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  transition: width 0.5s ease;
}

.session-details {
  margin-bottom: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .label {
  font-weight: 600;
  color: #6b7280;
  font-size: 0.875rem;
}

.detail-row.anomaly {
  color: #f59e0b;
  gap: 0.5rem;
}

.session-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-view,
.btn-approve {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.btn-view {
  background: #f3f4f6;
  color: #374151;
}

.btn-view:hover {
  background: #e5e7eb;
}

.btn-approve {
  background: #10b981;
  color: white;
}

.btn-approve:hover {
  background: #059669;
}

.btn-force-close,
.btn-reject {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem;
  border: 2px solid #ef4444;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  background: white;
  color: #ef4444;
}

.btn-force-close:hover,
.btn-reject:hover {
  background: #ef4444;
  color: white;
}

.btn-edit {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem;
  border: 2px solid #6366f1;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  background: white;
  color: #6366f1;
}

.btn-edit:hover {
  background: #6366f1;
  color: white;
}

.force-close-warning {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: #fef3c7;
  border-radius: 8px;
  color: #92400e;
  margin-bottom: 1rem;
  align-items: flex-start;
}

.force-close-warning p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.force-close-warning svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.force-close-info {
  background: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.filters {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.filter-group {
  flex: 1;
}

.filter-group label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.filter-group select,
.filter-group input {
  width: 100%;
  padding: 0.625rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
}

.filter-group select:focus,
.filter-group input:focus {
  outline: none;
  border-color: #667eea;
}

.btn-clear-filters {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
}

.btn-clear-filters:hover {
  background: #e5e7eb;
}

.table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.requests-table {
  width: 100%;
  border-collapse: collapse;
}

.requests-table thead {
  background: #f9fafb;
}

.requests-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  border-bottom: 2px solid #e5e7eb;
}

.requests-table td {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.875rem;
}

.requests-table tr:last-child td {
  border-bottom: none;
}

.employee-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.employee-cell strong {
  color: #1f2937;
}

.employee-cell span {
  color: #6b7280;
  font-size: 0.75rem;
}

.empty-row {
  text-align: center;
  padding: 3rem !important;
}

.empty-row svg {
  margin: 0 auto 0.5rem;
  color: #d1d5db;
}

.empty-row p {
  color: #9ca3af;
  margin: 0;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.approved {
  background: #dcfce7;
  color: #166534;
}

.status-badge.active {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.under_review {
  background: #dbeafe;
  color: #1e40af;
}

.status-badge.expired {
  background: #fee2e2;
  color: #991b1b;
}

.status-badge.payroll_approved {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.rejected {
  background: #fee2e2;
  color: #991b1b;
}

.session-type-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #f3f4f6;
  color: #374151;
}

.session-type-badge.post_shift {
  background: #dbeafe;
  color: #1e40af;
}

.session-type-badge.pre_shift {
  background: #e0e7ff;
  color: #3730a3;
}

.session-type-badge.weekend {
  background: #fef3c7;
  color: #92400e;
}

.session-type-badge.holiday {
  background: #fce7f3;
  color: #9d174d;
}

.session-type-badge.emergency {
  background: #fee2e2;
  color: #991b1b;
}

.btn-icon {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s;
}

.btn-icon:hover {
  color: #374151;
}

.btn-icon.success {
  color: #10b981;
}

.btn-icon.success:hover {
  color: #059669;
}

.btn-icon.danger {
  color: #ef4444;
}

.btn-icon.danger:hover {
  color: #dc2626;
}

.btn-icon.edit {
  color: #6366f1;
}

.btn-icon.edit:hover {
  color: #4f46e5;
}

.actions-cell {
  white-space: nowrap;
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  margin-top: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.btn-page {
  padding: 0.5rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-page:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 600;
  color: #374151;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content.large {
  max-width: 900px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
}

.form-group .hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.btn-cancel,
.btn-confirm,
.btn-danger {
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-cancel {
  background: #f3f4f6;
  color: #374151;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

.btn-confirm {
  background: #10b981;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: #059669;
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.detail-section {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.detail-section.full-width {
  grid-column: 1 / -1;
}

.detail-section h4 {
  font-size: 0.875rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item .label {
  font-weight: 600;
  color: #6b7280;
  font-size: 0.875rem;
}

.reason-text {
  color: #374151;
  line-height: 1.6;
  margin: 0;
}

.priority-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #dbeafe;
  color: #1e40af;
}

.evidence-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.anomaly-flags-section {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #fef3c7;
  border-radius: 8px;
  border: 1px solid #fde68a;
}

.anomaly-flags-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8rem;
  color: #92400e;
  margin-bottom: 0.5rem;
}

.anomaly-flags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.anomaly-flag-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  background: #fbbf24;
  color: #78350f;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { transform: translateX(400px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.toast.success {
  background: #10b981;
  color: white;
}

.toast.error {
  background: #ef4444;
  color: white;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
}

@media (max-width: 768px) {
  .overtime-clock-management {
    padding: 1rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .sessions-grid {
    grid-template-columns: 1fr;
  }

  .filters {
    flex-direction: column;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .photos-grid {
    grid-template-columns: 1fr;
  }

  .location-maps-grid {
    grid-template-columns: 1fr;
  }
}

.location-maps-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
}

.location-map-item h5 {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
}

.text-xs {
  font-size: 0.75rem;
}

.text-gray-400 {
  color: #9ca3af;
}

.iframe-map-wrapper {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
}

.map-controls-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
}

.map-provider-select {
  margin-left: auto;
  font-size: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  padding: 0.15rem 0.4rem;
  background: white;
}

.location-map-iframe {
  width: 100%;
  height: 220px;
  border: none;
}

.map-coords {
  padding: 0.4rem 0.75rem;
  font-size: 0.7rem;
  font-family: monospace;
  color: #6b7280;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}
</style>
